import React from "react";
import module_style from "./style.module.css";
import style from "../../style.module.css";
import { Input, Button } from "@my-scoot/component-library-legacy";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import AddCircle from "@material-ui/icons/AddCircle";
import ExlyModal from "common/Components/ExlyModal";
import { is_empty } from "utils/validations";
import { api, dataProvider } from "data";
import constants from "constants/constants";
import {
  HEADING_MAX_LENGTH,
  SIDEBAR_KEYS,
  SECTIONKEY_TO_MISC_KEY,
} from "ui/pages/TemplatePreview/constants.js";
import { useNotifications } from "utils/hooks";
import AwardsList from "./Components/AwardsList";
import { getSelectedData, refreshIframe, validateData } from "./Utils";
import moment from "moment";
import { WebpageCreationContext } from "ui/pages/TemplatePreview";
import { creator_miscellaneous_data_types } from "features/Common/modules/CreatorData/CreatorData.constants";
import SectionVisibilityToggle from "../hideShow";
import { handleVisibilityToggle } from "../../utils";
import useVisibilityMutations from "../../useVisibilityMutations.hook";

const Awards = ({ disableAllEdits }) => {
  const { notify } = useNotifications();
  const { miscellaneous_data, fetchMiscellaneousData, set_miscellaneous_data } =
    React.useContext(WebpageCreationContext);

  const [section_name, set_section_name] = React.useState(
    "Awards & certifications"
  );
  const [show_modal, set_show_modal] = React.useState(false);
  const [uuid, set_uuid] = React.useState(null);
  const [processing, set_processing] = React.useState(false);
  const [selected_id, set_selected_id] = React.useState(null);
  const [data, set_data] = React.useState([]);
  const [error_object, set_error_object] = React.useState({});
  const [visible, set_visible] = React.useState(true);
  const draftDataRef = React.useRef(null);

  // Mutations for visibility toggle
  const { createMutation, updateMutation } = useVisibilityMutations();

  React.useEffect(() => {
    assignData();
  }, [miscellaneous_data]);

  const assignData = async () => {
    try {
      if (is_empty(miscellaneous_data?.awards)) return;
      let temp = miscellaneous_data?.awards[0];
      set_data(temp?.data?.data);
      const backendSectionName = temp?.data?.section_name;

      set_section_name(
        backendSectionName && backendSectionName.trim()
          ? backendSectionName
          : section_name
      );

      set_uuid(temp?.uuid);

      // SAFETY NET: default visible = true
      set_visible(temp?.data?.visible ?? true);
    } catch (err) {
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        "error"
      );
    }
  };

  const handleSave = async ({
    currentData,
    selectedId,
    currentUuid,
    isVisible,
    currentSectionName,
  }) => {
    try {
      if (processing) return;
      set_processing(true);

      const draft = draftDataRef?.current;

      const hasAllDraftData =
        draft?.award_name || draft?.organisation_name || draft?.year;

      const hasActualData =
        Array.isArray(currentData) &&
        currentData.some(
          (item) =>
            !item.draft &&
            (item.award_name || item.organisation_name || item.year)
        );

      if (!hasActualData && !hasAllDraftData) {
        set_show_modal(false);
        set_selected_id(null);
        return;
      }

      const selected_data = Number.isInteger(selectedId)
        ? getSelectedData(currentData, selectedId) || draftDataRef.current
        : null;

      if (selected_data) {
        const error_obj = validateData(selected_data);

        if (!is_empty(error_obj)) {
          set_error_object(error_obj);
          notify("Invalid form values", "error");
          return;
        }
      }

      let temp = [...currentData];

      if (temp[selectedId]?.draft) {
        delete temp[selectedId].draft;
      }

      let payload, api_resource;

      if (is_empty(currentUuid)) {
        api_resource = api.creator_miscellaneous_data.create;
        payload = {
          type: creator_miscellaneous_data_types.AWARDS_CERTIFICATES,
          data: {
            section_name: currentSectionName,
            visible: isVisible,
            data: temp,
          },
        };
      } else {
        api_resource = api.creator_miscellaneous_data.update;
        payload = {
          uuid: currentUuid,
          updated_values: {
            data: {
              section_name: currentSectionName,
              visible: isVisible,
              data: temp,
            },
          },
        };
      }

      const status = await dataProvider.custom_request(
        api_resource,
        "POST",
        payload
      );

      if (status.status === 200) {
        refreshIframe();
        set_data(temp);
        set_show_modal(false);
        set_selected_id(null);
      }

      notify(status?.message);
    } catch (err) {
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        "error"
      );
    } finally {
      set_processing(false);
    }
  };

  const handleDelete = () => {
    if (!Number.isInteger(selected_id)) return;

    const temp = [...data];

    temp.splice(selected_id, 1);

    const updatedData = temp.map((item, index) => ({
      ...item,
      id: index,
    }));

    set_data(updatedData);

    handleSave({
      currentData: updatedData,
      selectedId: null,
      currentUuid: uuid,
      isVisible: visible,
      currentSectionName: section_name,
    });
  };

  const updateData = (value, key) => {
    let temp = data;
    temp[selected_id][key] = value;
    set_data([...temp]);
  };

  const handleModalClose = () => {
    set_show_modal(false);
    set_error_object({});
    set_selected_id(null);

    // remove draft entries
    let published_data = data.filter((item) => !item.draft);
    set_data([...published_data]);
  };

  const [sectionNameLimitHit, setSectionNameLimitHit] = React.useState(false);

  const onToggleVisibility = () =>
    handleVisibilityToggle({
      currentVisible: visible,
      setVisible: set_visible,
      miscellaneousData: miscellaneous_data,
      sectionKey: SECTIONKEY_TO_MISC_KEY[SIDEBAR_KEYS.AWARDS],
      sectionType: creator_miscellaneous_data_types.AWARDS_CERTIFICATES,
      currentSectionName: section_name,
      fetchMiscellaneousData,
      set_miscellaneous_data,
      createMutation: createMutation,
      updateMutation: updateMutation,
      currentData: data,
    });

  return (
    <div>
      <SectionVisibilityToggle
        visible={visible}
        disabled={disableAllEdits}
        onToggle={onToggleVisibility}
      />

      <div className={style.label}>
        What would you like to call this section?
      </div>

      <div className={`mt-2`}>
        <Input
          fullWidth
          value={section_name}
          inputProps={{
            maxLength: HEADING_MAX_LENGTH,
          }}
          error={sectionNameLimitHit}
          helperText={
            sectionNameLimitHit
              ? `Maximum field length of ${HEADING_MAX_LENGTH} reached.`
              : null
          }
          onChange={(e) => {
            const value = e.target.value;

            set_section_name(value);
            setSectionNameLimitHit(value.length >= HEADING_MAX_LENGTH);
          }}
          disabled={disableAllEdits}
        />
      </div>

      <div className={`${style.info} tw-my-[16px]`}>
        <InfoOutlined className={`${style.infoIcon}`} />
        The items can be dragged up and down using the &#10303; icon to change
        the order in which they appear.
      </div>

      {!is_empty(data) && (
        <AwardsList
          data={data}
          set_show_modal={(value) => set_show_modal(value)}
          set_data={(value) => set_data(value)}
          uuid={uuid}
          handleDelete={() => handleDelete()}
          selected_id={selected_id}
          set_selected_id={(value) => set_selected_id(value)}
          disableAllEdits={disableAllEdits}
        />
      )}

      <div className={`d-flex mt-4 align-items-center justify-content-between`}>
        {disableAllEdits ? (
          <div />
        ) : (
          <div
            onClick={() => {
              if (processing) return;
              let initial_data = {
                id: (data || []).length,
                award_name: "",
                organisation_name: "",
                year: null,
                draft: true,
              };
              const newSelectedId = data.length;
              set_selected_id(newSelectedId);
              set_data([...data, initial_data]);
              draftDataRef.current = initial_data;
              set_show_modal(true);
            }}
            className={`${module_style.action_color} ${module_style.text_cta} pointer`}
          >
            <AddCircle />
            <span className={`ml-2`}>Add award/certification</span>
          </div>
        )}

        <Button
          variant="contained"
          color="primary"
          disableElevation
          disabled={processing || disableAllEdits}
          loading={processing}
          onClick={() =>
            handleSave({
              currentData: data,
              selectedId: selected_id,
              currentUuid: uuid,
              isVisible: visible,
              currentSectionName: section_name,
            })
          }
        >
          Save
        </Button>
      </div>

      <ExlyModal
        title="Add an award/certification"
        open={show_modal}
        onClose={handleModalClose}
        primaryBtnText={processing ? "..." : "Save"}
        onPrimaryBtnClick={() =>
          handleSave({
            currentData: data,
            selectedId: selected_id,
            currentUuid: uuid,
            isVisible: visible,
            currentSectionName: section_name,
          })
        }
        modal_props={{
          modalPaperClassName: module_style.miscellaneous_data_modal,
        }}
      >
        {Number.isInteger(selected_id) && data[selected_id] && (
          <div className={`pt-4 pb-4`}>
            <div className={`${style.label} ${style.required} mb-1`}>
              Name of the Award/Certification
            </div>
            <Input
              error={error_object?.award_name}
              helperText={error_object?.award_name ? "Invalid" : null}
              value={data[selected_id]?.award_name}
              fullWidth
              onChange={(e) => {
                if (error_object?.award_name) {
                  set_error_object({ ...error_object, award_name: false });
                }

                let value = e.target.value;
                updateData(value, "award_name");
              }}
            />

            <div className={`${style.label} ${style.required} mt-3 mb-1`}>
              Name of the awarding institution
            </div>
            <Input
              error={error_object?.organisation_name}
              helperText={error_object?.organisation_name ? "Invalid" : null}
              fullWidth
              value={data[selected_id]?.organisation_name}
              onChange={(e) => {
                if (error_object?.organisation_name) {
                  set_error_object({
                    ...error_object,
                    organisation_name: false,
                  });
                }
                let value = e.target.value;
                updateData(value, "organisation_name");
              }}
            />

            <div className={`${style.label} ${style.required} mt-3 mb-1`}>
              Year of award recieval
            </div>

            <Input
              type="number"
              error={error_object?.year}
              helperText={error_object?.year ? "Invalid" : null}
              fullWidth
              value={data[selected_id]?.year}
              onChange={(e) => {
                if (error_object?.year) {
                  set_error_object({ ...error_object, year: false });
                }
                let value = e.target.value;
                if (value.length < 5) {
                  updateData(value, "year");
                }
                if (
                  !moment(value, "YYYY").isValid() ||
                  moment(value, "YYYY").isAfter(moment())
                ) {
                  set_error_object({ ...error_object, year: true });
                }
              }}
            />
          </div>
        )}
      </ExlyModal>
    </div>
  );
};

export default Awards;
